import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  AccordionComponent,
  AccordionItemComponent,
  ConfirmDialogComponent,
  EntityDetailLayoutComponent,
  ErrorStateComponent,
  LoadingStateComponent,
  PageActionsDirective,
  ProfileSummaryCardComponent,
  SelectionRowComponent,
  type ProfileDangerAction,
  type ProfileQuickAction,
  ToastService,
  ZagButton,
  ZagDatePipe,
  ZagIcon,
} from '@zag/ui';
import { Dialog } from '@angular/cdk/dialog';
import { UsersManagementService } from '../users-management.service';
import { RolesManagementService } from '../roles-management.service';

@Component({
  selector: 'sc-invitation-edit',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    SelectionRowComponent,
    AccordionComponent,
    AccordionItemComponent,
    ZagButton,
    ZagDatePipe,
    ZagIcon,
    LoadingStateComponent,
    ErrorStateComponent,
    PageActionsDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './invitation-edit.component.html',
  styleUrl: './invitation-edit.component.scss',
})
export class InvitationEditComponent {
  private readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(Dialog);
  private readonly usersService = inject(UsersManagementService);
  private readonly rolesService = inject(RolesManagementService);
  private readonly fb = inject(FormBuilder);

  /** Tracks language changes so computed signals using translate.instant() re-evaluate on switch */
  private readonly currentLang = toSignal(this.translate.onLangChange);

  /** Route params as signal */
  private readonly routeParams = toSignal(this.route.paramMap);
  readonly invitationId = computed(() => this.routeParams()?.get('id') ?? null);

  /** Loading & Error state */
  readonly isLoading = signal(true);
  readonly error = signal<string | null>(null);
  readonly isSaving = signal(false);

  /** Form with invitation data */
  protected readonly form: FormGroup<{
    email: FormControl<string>;
    name: FormControl<string>;
    message: FormControl<string>;
  }> = this.fb.nonNullable.group({
    email: [{ value: '', disabled: true }, [Validators.required, Validators.email]],
    name: [''],
    message: [''],
  });

  /** Track selected role IDs */
  protected readonly selectedRoleIds = signal<Set<string>>(new Set());

  /** Available roles from backend */
  protected readonly availableRoles = computed(() => this.rolesService.roles());
  protected readonly rolesLoading = computed(() => this.rolesService.isLoading());

  /** Profile card data */
  protected readonly invitationEmail = signal('');
  protected readonly invitationName = signal<string | undefined>(undefined);
  protected readonly createdAt = signal<string | undefined>(undefined);
  protected readonly inviterName = signal<string | undefined>(undefined);

  protected readonly dangerActions = computed<ProfileDangerAction[]>(() => {
    this.currentLang(); // re-evaluate when language changes
    return [
      {
        id: 'revoke',
        label: this.translate.instant('settings.users.invite.revoke.button'),
        icon: 'delete',
        disabled: false,
      },
    ];
  });

  protected readonly quickActions = computed<ProfileQuickAction[]>(() => {
    this.currentLang(); // re-evaluate when language changes
    return [
      {
        id: 'resend',
        label: this.translate.instant('settings.users.actions.resend'),
        icon: 'forward_to_inbox',
        disabled: false,
      },
    ];
  });

  constructor() {
    this.rolesService.reload();

    // Load invitation data when ID changes
    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(() => {
      this.loadInvitation();
    });
  }

  private loadInvitation(): void {
    const id = this.invitationId();
    if (!id) return;

    this.isLoading.set(true);
    this.error.set(null);

    this.usersService.getInvitation(id).subscribe({
      next: data => {
        this.invitationEmail.set(data.email);
        this.invitationName.set(data.name ?? undefined);
        this.createdAt.set(typeof data.createdAt === 'string' ? data.createdAt : data.createdAt.toISOString());
        this.inviterName.set(data.inviterName);

        this.form.patchValue({
          email: data.email,
          name: data.name ?? '',
          message: data.message ?? '',
        });

        this.selectedRoleIds.set(new Set(data.roleIds ?? []));
        this.isLoading.set(false);
      },
      error: () => {
        this.error.set(this.translate.instant('settings.users.loadError'));
        this.isLoading.set(false);
      },
    });
  }

  protected onRoleToggle(roleId: string, checked: boolean): void {
    const selected = new Set(this.selectedRoleIds());
    if (checked) {
      selected.add(roleId);
    } else {
      selected.delete(roleId);
    }
    this.selectedRoleIds.set(selected);
  }

  protected saveAll(): void {
    const id = this.invitationId();
    if (!id || !this.form.valid || this.isSaving()) return;

    this.isSaving.set(true);

    const nameValue = this.form.getRawValue().name.trim();
    const payload = {
      email: this.invitationEmail(), // use original email
      message: this.form.getRawValue().message.trim() || undefined,
      roleIds: Array.from(this.selectedRoleIds()),
      ...(nameValue && { name: nameValue }),
    };

    // Reconfiguring an invitation is essentially creating a new one with same email (revokes old one)
    this.usersService.createInvitation(payload).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('settings.users.invite.success'));
        this.isSaving.set(false);
        this.onCancel(); // Navigate back
      },
      error: (err: unknown) => {
        this.isSaving.set(false);
        const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : null;
        this.toast.error(message || this.translate.instant('settings.users.saveError'));
      },
    });
  }

  protected onCancel(): void {
    const returnUrl = this.router.url.includes('/admin/') ? '/admin/users' : '/settings/users';
    void this.router.navigate([returnUrl]);
  }

  protected onQuickAction(action: ProfileQuickAction): void {
    if (action.id === 'resend') {
      this.onResend();
    }
  }

  protected onDangerAction(action: ProfileDangerAction): void {
    if (action.id === 'revoke') {
      this.onRevoke();
    }
  }

  private onResend(): void {
    const id = this.invitationId();
    if (!id) return;

    this.usersService.resendInvitation(id).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('settings.users.invite.resend.success'));
      },
      error: () => {
        this.toast.error(this.translate.instant('settings.users.invite.resend.error'));
      },
    });
  }

  private onRevoke(): void {
    const id = this.invitationId();
    if (!id) return;

    const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, {
      width: 'min(96vw, 800px)',
      maxWidth: '96vw',
      panelClass: 'ai-chat-dialog-panel',
      backdropClass: 'ai-chat-dialog-backdrop',
      data: {
        title: this.translate.instant('settings.users.invite.revoke.confirm.title'),
        message: this.translate.instant('settings.users.invite.revoke.confirm.message'),
        confirmText: this.translate.instant('common.delete'),
        destructive: true,
      },
    });

    dialogRef.closed.subscribe(confirmed => {
      if (!confirmed) return;

      this.usersService.revokeInvitation(id).subscribe({
        next: () => {
          this.toast.success(this.translate.instant('settings.users.invite.revoke.success'));
          this.onCancel();
        },
        error: () => {
          this.toast.error(this.translate.instant('settings.users.invite.revoke.error'));
        },
      });
    });
  }
}
